import { useState, useEffect, useCallback } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

// slides: [{ img, title, text }] - visite guidée dans la fenêtre agrandie
const ImageLightbox = ({ src, alt, className = "", children, slides, startIndex = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(startIndex);
  const list = slides && slides.length ? slides : [{ img: src, title: alt, text: "" }];
  const current = list[Math.min(index, list.length - 1)];

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + list.length) % list.length),
    [list.length]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, go]);

  const open = () => {
    setIndex(startIndex);
    setIsOpen(true);
  };

  return (
    <>
      <div className={`relative group cursor-cayus-zoom ${className}`} onClick={open} data-testid="lightbox-trigger">
        {children}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-2xl flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 rounded-full p-2 shadow-lg">
            <ZoomIn className="w-5 h-5 text-[#3A3F4B]" />
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-[#0A2342]/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-8"
          onClick={() => setIsOpen(false)}
          data-testid="lightbox-overlay"
        >
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <div className="flex items-center justify-center w-full px-12 sm:px-0" onClick={(e) => e.stopPropagation()}>
            {list.length > 1 && (
              <button
                onClick={() => go(-1)}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors z-10"
                data-testid="lightbox-prev"
                aria-label="Précédent"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            <img
              src={current.img}
              alt={current.title}
              className="max-w-full sm:max-w-[76vw] max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg shadow-2xl bg-white"
              data-testid="lightbox-image"
            />
            {list.length > 1 && (
              <button
                onClick={() => go(1)}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors z-10"
                data-testid="lightbox-next"
                aria-label="Suivant"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            )}
          </div>

          {/* Commentaire de la visite guidée */}
          <div className="mt-6 max-w-2xl text-center" onClick={(e) => e.stopPropagation()} data-testid="lightbox-caption">
            {list.length > 1 && (
              <p className="mono-cayus text-[10px] text-[#9ECEFA] mb-2">
                {index + 1} / {list.length}
              </p>
            )}
            {current.title && <p className="font-display font-semibold text-lg text-white mb-1">{current.title}</p>}
            {current.text && <p className="font-body-cayus text-sm text-[#E4EEFC] leading-relaxed">{current.text}</p>}
          </div>

          {list.length > 1 && (
            <div className="mt-5 flex gap-2" onClick={(e) => e.stopPropagation()}>
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#0047AB]" : "w-4 bg-white/25 hover:bg-white/40"}`}
                  data-testid={`lightbox-dot-${i}`}
                  aria-label={`Écran ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ImageLightbox;
